import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Eye, EyeOff, ExternalLink, FileText } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { supabase } from "@/integrations/supabase/client";
import {
  listMyEvidence, createEvidence, updateEvidence, deleteEvidence,
  createEvidenceUploadUrl, createEvidenceDownloadUrl,
} from "@/lib/api/lxp-evidence.functions";

const BUCKET = "lxp-portfolio-evidence";

export const Route = createFileRoute("/_authenticated/lexpractiq/portfolio-evidence")({
  head: () => ({ meta: [{ title: "Portfolio Evidence | SA LexPractiq" }] }),
  component: EvidencePage,
});

function EvidencePage() {
  const listFn = useServerFn(listMyEvidence);
  const createFn = useServerFn(createEvidence);
  const updateFn = useServerFn(updateEvidence);
  const delFn = useServerFn(deleteEvidence);
  const uploadUrlFn = useServerFn(createEvidenceUploadUrl);
  const downloadUrlFn = useServerFn(createEvidenceDownloadUrl);

  const [items, setItems] = useState<any[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"document" | "link" | "certificate" | "award" | "publication">("document");
  const [file, setFile] = useState<File | null>(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [competency, setCompetency] = useState("");
  const [publicVis, setPublicVis] = useState(false);
  const [busy, setBusy] = useState(false);

  async function reload() { const r = await listFn(); setItems(r.items); }
  useEffect(() => { reload(); }, []);

  async function submit() {
    if (title.length < 2) return toast.error("Title required");
    setBusy(true);
    try {
      let file_path: string | undefined;
      if (file) {
        const u = await uploadUrlFn({ data: { filename: file.name } });
        const { error } = await supabase.storage
          .from(BUCKET)
          .uploadToSignedUrl(u.path, u.token, file);
        if (error) throw new Error(error.message);
        file_path = u.path;
      }
      await createFn({ data: {
        title, description: description || undefined, evidence_type: type,
        file_path, external_url: externalUrl || undefined,
        competency_key: competency || undefined, public: publicVis,
      } });
      toast.success("Evidence added");
      setTitle(""); setDescription(""); setFile(null); setExternalUrl(""); setCompetency(""); setPublicVis(false);
      await reload();
    } catch (e: any) { toast.error(e?.message || "Failed"); }
    finally { setBusy(false); }
  }

  async function open(path: string) {
    try { const r = await downloadUrlFn({ data: { path } }); window.open(r.url, "_blank"); }
    catch (e: any) { toast.error(e?.message); }
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem" }}>
          <div>
            <h1>Portfolio evidence</h1>
            <p className="lxp-muted">Upload contracts you've drafted, memos, awards, and certificates. Mark items public to show recruiters.</p>
          </div>
          <Link to="/lexpractiq/dashboard" className="lxp-btn-ghost">← Dashboard</Link>
        </div>

        <div className="lxp-card" style={{ marginBottom: "1.25rem" }}>
          <h3 style={{ margin: "0 0 0.5rem" }}>Add new evidence</h3>
          <div style={{ display: "grid", gap: "0.6rem", gridTemplateColumns: "1fr 1fr" }}>
            <label style={{ gridColumn: "span 2" }}>
              <div className="lxp-muted">Title</div>
              <input className="lxp-input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label style={{ gridColumn: "span 2" }}>
              <div className="lxp-muted">Description</div>
              <textarea className="lxp-textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label>
              <div className="lxp-muted">Type</div>
              <select className="lxp-input" value={type} onChange={(e) => setType(e.target.value as any)}>
                <option value="document">Document</option>
                <option value="certificate">Certificate</option>
                <option value="award">Award</option>
                <option value="publication">Publication</option>
                <option value="link">External link</option>
              </select>
            </label>
            <label>
              <div className="lxp-muted">Competency tag (optional)</div>
              <input className="lxp-input" placeholder="Drafting / Research / Advocacy" value={competency} onChange={(e) => setCompetency(e.target.value)} />
            </label>
            <label>
              <div className="lxp-muted">Upload file (PDF, DOCX, image)</div>
              <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <label>
              <div className="lxp-muted">Or external URL</div>
              <input className="lxp-input" placeholder="https://…" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", gridColumn: "span 2" }}>
              <input type="checkbox" checked={publicVis} onChange={(e) => setPublicVis(e.target.checked)} />
              Make this evidence public to recruiters
            </label>
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <button className="lxp-btn" onClick={submit} disabled={busy}>
              <Upload size={14} /> {busy ? "Uploading…" : "Add evidence"}
            </button>
          </div>
        </div>

        <h3>My evidence ({items.length})</h3>
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {items.map((it) => (
            <div key={it.id} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: "0.75rem" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                  <FileText size={14} />
                  <strong>{it.title}</strong>
                  <span className="lxp-badge">{it.evidence_type}</span>
                  {it.competency_key && <span className="lxp-badge">{it.competency_key}</span>}
                </div>
                {it.description && <p style={{ margin: "0.3rem 0", fontSize: "0.85rem" }}>{it.description}</p>}
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.4rem" }}>
                  {it.file_path && <button className="lxp-btn-ghost" onClick={() => open(it.file_path)} style={{ fontSize: "0.8rem" }}>Open file</button>}
                  {it.external_url && <a className="lxp-btn-ghost" href={it.external_url} target="_blank" rel="noreferrer" style={{ fontSize: "0.8rem" }}>Visit <ExternalLink size={12} /></a>}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <button
                  className="lxp-btn-ghost"
                  onClick={async () => { await updateFn({ data: { id: it.id, public: !it.public } }); await reload(); }}
                  style={{ fontSize: "0.75rem" }}
                >
                  {it.public ? <><Eye size={12} /> Public</> : <><EyeOff size={12} /> Private</>}
                </button>
                <button
                  className="lxp-btn-ghost"
                  onClick={async () => { if (confirm("Delete this evidence?")) { await delFn({ data: { id: it.id } }); await reload(); } }}
                  style={{ fontSize: "0.75rem", color: "var(--lxp-warn)" }}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="lxp-muted">No evidence yet. Upload your first item above.</p>}
        </div>
      </div>
    </LxpLayout>
  );
}
